import { Button, Group, Loader, Menu } from "@mantine/core"
import {
  IconCopy,
  IconDeviceFloppy,
  IconEdit,
  IconFolderOpen,
  IconSettings,
  IconShare,
} from "@tabler/icons-react"
import { ReactNode } from "react"

type ExportMenuItem = {
  label: string
  icon: ReactNode
  onClick?: () => void
  disabled?: boolean
}

type SettingsMenuItem = {
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  color?: string
}

type UnifiedActionBarProps = {
  // Common props
  isExisting: boolean
  isEditing?: boolean

  // Open/Duplicate handlers
  onOpen: () => void
  onDuplicate: () => void

  // Settings menu
  settingsItems?: SettingsMenuItem[]

  // Export menu
  exportItems: ExportMenuItem[]

  // Edit functionality
  showEditButton?: boolean
  onEdit?: () => void

  // Save functionality
  showSaveButton?: boolean
  onSave?: () => void
  saveButtonLabel?: string
  saveButtonLoading?: boolean

  // Discard functionality
  showDiscardButton?: boolean
  onDiscard?: () => void

  // Edit mode content (replaces the entire action bar when isEditing is true)
  editModeContent?: ReactNode

  // Additional content
  rightContent?: ReactNode
}

export const UnifiedActionBar = ({
  isExisting,
  isEditing = false,
  onOpen,
  onDuplicate,
  settingsItems,
  exportItems,
  showEditButton = false,
  onEdit,
  showSaveButton = false,
  onSave,
  saveButtonLabel = "Save",
  saveButtonLoading = false,
  showDiscardButton = false,
  onDiscard,
  editModeContent,
  rightContent,
}: UnifiedActionBarProps) => {
  return (
    <>
      {isEditing && editModeContent ? (
        editModeContent
      ) : (
        <Group gap={4} justify="space-between">
          <Group gap={0}>
            <Button.Group>
              <Button
                variant="transparent"
                c="gray"
                leftSection={<IconFolderOpen />}
                onClick={onOpen}
              >
                Open
              </Button>
              {isExisting && (
                <Button
                  variant="transparent"
                  c="gray"
                  leftSection={<IconCopy />}
                  onClick={onDuplicate}
                >
                  Clone
                </Button>
              )}
            </Button.Group>

            {settingsItems && settingsItems.length > 0 && isExisting && (
              <Menu shadow="md" position="bottom-start">
                <Menu.Target>
                  <Button
                    variant="transparent"
                    c="gray"
                    leftSection={<IconSettings />}
                  >
                    Settings
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {settingsItems.map((item, index) => (
                    <Menu.Item
                      key={index}
                      c={item.color}
                      leftSection={item.icon}
                      onClick={item.onClick}
                      disabled={item.disabled}
                      color={item.color}
                    >
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}

            {isExisting && (
              <Menu shadow="md" position="bottom-start">
                <Menu.Target>
                  <Button
                    variant="transparent"
                    c="gray"
                    leftSection={<IconShare />}
                  >
                    Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {exportItems.map((item, index) => (
                    <Menu.Item
                      key={index}
                      leftSection={item.icon}
                      onClick={item.onClick}
                      disabled={item.disabled}
                    >
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>

          <Group gap={4}>
            {showEditButton && !isEditing && isExisting && (
              <Button
                variant="transparent"
                c="gray"
                leftSection={<IconEdit />}
                onClick={onEdit}
              >
                Edit Layout
              </Button>
            )}

            {rightContent}

            {showDiscardButton && (
              <Button variant="transparent" c="gray" onClick={onDiscard}>
                Discard
              </Button>
            )}

            {showSaveButton &&
              (saveButtonLoading ? (
                <Loader size="sm" />
              ) : (
                <Button leftSection={<IconDeviceFloppy />} onClick={onSave}>
                  {saveButtonLabel}
                </Button>
              ))}
          </Group>
        </Group>
      )}
    </>
  )
}
